import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getSalesPersons, createSalesPerson, updateSalesPerson, deleteSalesPerson, getSalesPersonLedger, getSalesPersonLedgerStats, getUserKYC, updateUserKYC, verifyUserKYC, getSalesPersonDashboard, recordSalesPersonPayment } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, MagnifyingGlass, PencilSimple, Trash, UserCircle, CurrencyInr, ShoppingCart, ArrowUp, ArrowDown, X, IdentificationCard, CheckCircle, Clock, Buildings, Wallet } from '@phosphor-icons/react';
import { toast } from 'sonner';
import SearchBar from '../components/SearchBar';

export default function SalesPersonPage() {
  const { user } = useAuth();
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [ledgerStats, setLedgerStats] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({ name: '', mobile: '', email: '', password: '', commission_rate: '' });
  const [editForm, setEditForm] = useState({ name: '', mobile: '', email: '', status: '', commission_rate: '' });

  // KYC state
  const [kycOpen, setKycOpen] = useState(false);
  const [kycPerson, setKycPerson] = useState(null);
  const [kycData, setKycData] = useState({ kyc_type: 'none', kyc_status: 'pending', pan_number: '', gst_number: '', documents: [] });
  const [kycForm, setKycForm] = useState({ kyc_type: 'mini', pan_number: '', gst_number: '' });
  const [kycSubmitting, setKycSubmitting] = useState(false);

  // Dashboard state (for sales person view)
  const [spDashboard, setSpDashboard] = useState(null);

  // Payment form state
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_mode: 'cash', reference: '', notes: '' });
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);

  const isOwner = user?.role === 'admin' || user?.role === 'owner';
  const isAdminOrAbove = isOwner || user?.role === 'assistant';
  const isAccountant = user?.role === 'accountant';
  const canViewPayments = isOwner || isAdminOrAbove || isAccountant;
  const isSalesPerson = user?.role === 'sales_person';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchQuery.trim()) params.q = searchQuery.trim();
      if (filterStatus !== 'all') params.status = filterStatus;
      const res = await getSalesPersons(params);
      setPersons(res.data);
      // Fetch dashboard for sales person
      if (isSalesPerson) {
        try {
          const dashRes = await getSalesPersonDashboard();
          setSpDashboard(dashRes.data);
        } catch (err) { console.error('Agency load failed:', err?.message); }
      }
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [searchQuery, filterStatus, isSalesPerson]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile || !form.email || !form.password) { toast.error('All fields required'); return; }
    setSubmitting(true);
    try {
      await createSalesPerson({ ...form, commission_rate: parseFloat(form.commission_rate) || 0 });
      toast.success('Sales person created!');
      setCreateOpen(false);
      setForm({ name: '', mobile: '', email: '', password: '', commission_rate: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Create failed'); }
    finally { setSubmitting(false); }
  };

  const openEdit = (p) => {
    setSelectedPerson(p);
    setEditForm({ name: p.name, mobile: p.mobile, email: p.email, status: p.status, commission_rate: String(p.commission_rate || 0) });
    setEditOpen(true);
  };

  const handleEdit = async () => {
    setSubmitting(true);
    try {
      await updateSalesPerson(selectedPerson.id, { ...editForm, commission_rate: parseFloat(editForm.commission_rate) || 0 });
      toast.success('Updated!');
      setEditOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    try {
      await deleteSalesPerson(selectedPerson.id);
      toast.success('Deleted');
      setDeleteOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const openLedger = async (p) => {
    setSelectedPerson(p);
    setLedgerOpen(true);
    try {
      const [ledgerRes, statsRes] = await Promise.all([
        getSalesPersonLedger({ sp_id: p.id }),
        getSalesPersonLedgerStats({ sp_id: p.id }),
      ]);
      setLedgerEntries(ledgerRes.data);
      setLedgerStats(statsRes.data);
    } catch { toast.error('Ledger load failed'); }
  };

  const openKyc = async (p) => {
    setKycPerson(p);
    setKycOpen(true);
    try {
      const res = await getUserKYC(p.id);
      setKycData(res.data);
      setKycForm({ kyc_type: res.data.kyc_type === 'none' ? 'mini' : res.data.kyc_type, pan_number: res.data.pan_number || '', gst_number: res.data.gst_number || '' });
    } catch { setKycData({ kyc_type: 'none', kyc_status: 'pending', pan_number: '', gst_number: '', documents: [] }); console.error('KYC load failed'); }
  };

  const handleKycSubmit = async () => {
    setKycSubmitting(true);
    try {
      await updateUserKYC(kycPerson.id, kycForm);
      toast.success('KYC updated!');
      const res = await getUserKYC(kycPerson.id);
      setKycData(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'KYC update failed'); }
    finally { setKycSubmitting(false); }
  };

  const handleKycVerify = async () => {
    try {
      await verifyUserKYC(kycPerson.id);
      toast.success('KYC verified!');
      const res = await getUserKYC(kycPerson.id);
      setKycData(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Verify failed'); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(v || 0);

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!selectedPerson || !paymentForm.amount) return;
    setPaymentSubmitting(true);
    try {
      await recordSalesPersonPayment(selectedPerson.id, {
        amount: parseFloat(paymentForm.amount),
        payment_mode: paymentForm.payment_mode,
        reference: paymentForm.reference,
        notes: paymentForm.notes,
      });
      toast.success(`Payment ₹${paymentForm.amount} recorded`);
      setPaymentForm({ amount: '', payment_mode: 'cash', reference: '', notes: '' });
      // Refresh ledger
      const [ledgerRes, statsRes] = await Promise.all([
        getSalesPersonLedger({ sp_id: selectedPerson.id }),
        getSalesPersonLedgerStats({ sp_id: selectedPerson.id }),
      ]);
      setLedgerEntries(ledgerRes.data);
      setLedgerStats(statsRes.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Payment failed'); }
    finally { setPaymentSubmitting(false); }
  };

  return (
    <div className="space-y-5" data-testid="sales-person-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Sales Persons</h1>
        {!isSalesPerson && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700 self-start sm:self-auto" data-testid="add-sales-person-btn">
                <Plus size={18} className="mr-2" /> Add Sales Person
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Add Sales Person</DialogTitle></DialogHeader>
              <form onSubmit={handleCreate} className="space-y-3 mt-2">
                <div><Label>Name <span className="text-red-500">*</span></Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required data-testid="sp-name" /></div>
                <div><Label>Mobile <span className="text-red-500">*</span></Label><Input value={form.mobile} onChange={e => setForm(p => ({ ...p, mobile: e.target.value }))} required data-testid="sp-mobile" /></div>
                <div><Label>Email <span className="text-red-500">*</span></Label><Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required data-testid="sp-email" /></div>
                <div><Label>Password <span className="text-red-500">*</span></Label><Input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} required data-testid="sp-password" /></div>
                <div><Label>Commission Rate (per cylinder)</Label><Input type="number" step="0.01" value={form.commission_rate} onChange={e => setForm(p => ({ ...p, commission_rate: e.target.value }))} placeholder="e.g. 1.00" data-testid="sp-commission" /></div>
                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 h-11" disabled={submitting} data-testid="sp-create-btn">{submitting ? 'Creating...' : 'Create'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Sales Person Dashboard — Agency-wise Stats */}
      {isSalesPerson && spDashboard && (
        <div className="space-y-4" data-testid="sp-dashboard">
          {/* Commission Banner */}
          <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 border-2 border-emerald-300 rounded-lg p-3 sm:p-4 flex flex-wrap items-center justify-between gap-3" data-testid="sp-commission-banner">
            <div>
              <p className="text-[10px] text-emerald-700 uppercase font-semibold">My Commission Earned</p>
              <p className="text-2xl sm:text-3xl font-bold text-emerald-700">{formatCurrency(spDashboard.total_commission_confirmed || 0)}</p>
              <p className="text-[11px] text-emerald-800/70 mt-0.5">
                {spDashboard.total_confirmed_cylinders || 0} confirmed cylinders × ₹{spDashboard.commission_rate || 0}/cyl
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-amber-700 uppercase font-semibold">Pending Commission</p>
              <p className="text-xl sm:text-2xl font-bold text-amber-700">{formatCurrency(spDashboard.total_commission_pending || 0)}</p>
              <p className="text-[11px] text-amber-800/70 mt-0.5">
                {spDashboard.total_pending_cylinders || 0} pending cylinders × ₹{spDashboard.commission_rate || 0}/cyl
              </p>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white border border-gray-200 p-3 sm:p-4 rounded-lg">
              <p className="text-[10px] text-gray-500 uppercase font-semibold">Agencies</p>
              <p className="text-xl sm:text-2xl font-bold text-indigo-600">{spDashboard.total_agencies}</p>
            </div>
            <div className="bg-white border border-gray-200 p-3 sm:p-4 rounded-lg">
              <p className="text-[10px] text-gray-500 uppercase font-semibold">Total Cylinders</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{spDashboard.total_cylinders}</p>
            </div>
            <div className="bg-white border border-green-200 p-3 sm:p-4 rounded-lg">
              <p className="text-[10px] text-green-600 uppercase font-semibold">Confirmed</p>
              <p className="text-xl sm:text-2xl font-bold text-green-600">{spDashboard.total_confirmed}</p>
            </div>
            <div className="bg-white border border-amber-200 p-3 sm:p-4 rounded-lg">
              <p className="text-[10px] text-amber-600 uppercase font-semibold">Pending</p>
              <p className="text-xl sm:text-2xl font-bold text-amber-600">{spDashboard.total_pending}</p>
            </div>
          </div>

          {/* Agency-wise Breakdown Table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h2 className="text-sm font-bold text-gray-700 flex items-center gap-2"><Buildings size={16} /> Agency-wise Details</h2>
            </div>

            {/* Mobile Cards */}
            <div className="sm:hidden divide-y divide-gray-100">
              {spDashboard.agency_stats.map((ag, i) => (
                <div key={ag.agency_id || `ag-${i}`} className="p-3 space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-bold text-gray-900">{ag.agency_name}</p>
                      <p className="text-xs text-gray-500">Distributor: {ag.distributor_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold">{ag.total_cylinders} cyl</p>
                      <p className="text-xs text-gray-500">{ag.total_bookings} bookings</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center bg-gray-50 rounded p-2">
                    <div>
                      <p className="text-[10px] text-green-600 font-semibold">Confirmed</p>
                      <p className="text-sm font-bold text-green-700">{ag.confirmed_cylinders}</p>
                      <p className="text-[10px] text-gray-400">cyl</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-amber-600 font-semibold">Pending</p>
                      <p className="text-sm font-bold text-amber-700">{ag.pending_cylinders}</p>
                      <p className="text-[10px] text-gray-400">cyl</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-emerald-700 font-semibold">Commission</p>
                      <p className="text-sm font-bold text-emerald-700">{formatCurrency(ag.confirmed_commission || 0)}</p>
                      <p className="text-[10px] text-gray-400">@ ₹{ag.commission_rate || 0}/cyl</p>
                    </div>
                  </div>
                </div>
              ))}
              {spDashboard.agency_stats.length === 0 && <p className="text-center py-6 text-gray-400 text-sm">No agencies assigned</p>}
            </div>

            {/* Desktop Table */}
            <div className="hidden sm:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-xs">Agency</TableHead>
                    <TableHead className="text-xs">Distributor</TableHead>
                    <TableHead className="text-xs text-center">Bookings</TableHead>
                    <TableHead className="text-xs text-center">Cylinders</TableHead>
                    <TableHead className="text-xs text-center">Confirmed</TableHead>
                    <TableHead className="text-xs text-center">Pending</TableHead>
                    <TableHead className="text-xs text-center">Rate</TableHead>
                    <TableHead className="text-xs text-right">Commission</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {spDashboard.agency_stats.map((ag, i) => (
                    <TableRow key={ag.agency_id || `ag-row-${i}`}>
                      <TableCell className="font-semibold">{ag.agency_name}</TableCell>
                      <TableCell className="text-sm text-gray-500">{ag.distributor_name}</TableCell>
                      <TableCell className="text-center font-mono">{ag.total_bookings}</TableCell>
                      <TableCell className="text-center font-bold">{ag.total_cylinders}</TableCell>
                      <TableCell className="text-center">
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">{ag.confirmed_cylinders} cyl</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">{ag.pending_cylinders} cyl</span>
                      </TableCell>
                      <TableCell className="text-center font-mono text-xs">₹{ag.commission_rate || 0}/cyl</TableCell>
                      <TableCell className="text-right font-mono font-bold text-emerald-700">
                        {formatCurrency(ag.confirmed_commission || 0)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {spDashboard.agency_stats.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center py-6 text-gray-400">No agencies assigned</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      )}

      {/* Search + Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search name or mobile..."
          matchCount={persons.length}
          noun="sales persons"
          testId="sp-search"
          inputClassName="pl-8 pr-7 h-9 text-sm"
        />
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36 h-9 text-sm" data-testid="sp-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="approved">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? <div className="text-center py-8 text-gray-500">Loading...</div> : (
          <>
            <div className="sm:hidden divide-y divide-gray-100">
              {persons.length === 0 ? <p className="text-center py-8 text-gray-400 text-sm">No sales persons found</p> : persons.map(p => (
                <div key={p.id} className="p-3" data-testid={`sp-row-${p.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-semibold">{p.name}</p>
                      <p className="text-xs text-gray-500">{p.mobile} | {p.email}</p>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${p.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{p.status === 'approved' ? 'Active' : 'Inactive'}</span>
                  </div>
                  {/* Assigned Agencies */}
                  {p.assigned_agencies?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {p.assigned_agencies.map((a, i) => (
                        <span key={a.id || `agency-${i}`} className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">{a.name}</span>
                      ))}
                    </div>
                  )}
                  {/* Assigned Distributors */}
                  {p.assigned_distributors?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {p.assigned_distributors.map((d, i) => (
                        <span key={d.id || `dist-${i}`} className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{d.name}</span>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" variant="ghost" className="text-indigo-600 text-xs h-7" onClick={() => openLedger(p)}>Ledger</Button>
                    <Button size="sm" variant="ghost" className="text-amber-600 text-xs h-7" onClick={() => openKyc(p)}>KYC</Button>
                    {!isSalesPerson && <Button size="sm" variant="ghost" className="text-blue-600 text-xs h-7" onClick={() => openEdit(p)}>Edit</Button>}
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden sm:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-xs">Name</TableHead>
                    <TableHead className="text-xs">Mobile</TableHead>
                    <TableHead className="text-xs">Email</TableHead>
                    <TableHead className="text-xs">Assigned Agencies</TableHead>
                    <TableHead className="text-xs">Distributors</TableHead>
                    <TableHead className="text-xs text-right">Commission/Cyl</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {persons.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-400">No sales persons found</TableCell></TableRow> : persons.map(p => (
                    <TableRow key={p.id} data-testid={`sp-row-${p.id}`}>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell className="text-sm">{p.mobile}</TableCell>
                      <TableCell className="text-sm text-gray-500">{p.email}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {p.assigned_agencies?.map((a, i) => (
                            <span key={a.id || `pa-${i}`} className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">{a.name}</span>
                          ))}
                          {(!p.assigned_agencies || p.assigned_agencies.length === 0) && <span className="text-xs text-gray-400">None</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {p.assigned_distributors?.map((d, i) => (
                            <span key={d.id || `pd-${i}`} className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{d.name}</span>
                          ))}
                          {(!p.assigned_distributors || p.assigned_distributors.length === 0) && <span className="text-xs text-gray-400">—</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(p.commission_rate)}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-0.5 rounded font-semibold ${p.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{p.status === 'approved' ? 'Active' : 'Inactive'}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" className="text-indigo-600 h-7 text-xs" onClick={() => openLedger(p)} data-testid={`sp-ledger-${p.id}`}><CurrencyInr size={14} className="mr-1" />Ledger</Button>
                          <Button size="sm" variant="ghost" className="text-amber-600 h-7 text-xs" onClick={() => openKyc(p)} data-testid={`sp-kyc-${p.id}`}><IdentificationCard size={14} className="mr-1" />KYC</Button>
                          {!isSalesPerson && <Button size="sm" variant="ghost" className="text-blue-600 h-7" onClick={() => openEdit(p)} data-testid={`sp-edit-${p.id}`}><PencilSimple size={14} /></Button>}
                          {isOwner && <Button size="sm" variant="ghost" className="text-red-600 h-7" onClick={() => { setSelectedPerson(p); setDeleteOpen(true); }} data-testid={`sp-delete-${p.id}`}><Trash size={14} /></Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit Sales Person</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label>Name</Label><Input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} data-testid="sp-edit-name" /></div>
            <div><Label>Mobile</Label><Input value={editForm.mobile} onChange={e => setEditForm(p => ({ ...p, mobile: e.target.value }))} data-testid="sp-edit-mobile" /></div>
            <div><Label>Email</Label><Input type="email" value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} data-testid="sp-edit-email" /></div>
            <div><Label>Commission Rate (per cyl)</Label><Input type="number" step="0.01" value={editForm.commission_rate} onChange={e => setEditForm(p => ({ ...p, commission_rate: e.target.value }))} data-testid="sp-edit-commission" /></div>
            <div>
              <Label>Status</Label>
              <Select value={editForm.status} onValueChange={v => setEditForm(p => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="sp-edit-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="approved">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
            <Button onClick={handleEdit} className="w-full bg-blue-600 hover:bg-blue-700 h-11" disabled={submitting} data-testid="sp-edit-save">{submitting ? 'Saving...' : 'Save Changes'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedPerson?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="sp-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Ledger Dialog */}
      <Dialog open={ledgerOpen} onOpenChange={setLedgerOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedPerson && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UserCircle size={24} className="text-indigo-600" />
                  {selectedPerson.name} — Commission & Payments
                </DialogTitle>
              </DialogHeader>
              {ledgerStats && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3">
                  <div className="bg-green-50 border border-green-200 p-2.5 rounded-lg" data-testid="sp-total-earned">
                    <p className="text-[10px] text-green-600 uppercase font-semibold">Earned</p>
                    <p className="text-base sm:text-lg font-bold text-green-700">{formatCurrency(ledgerStats.total_earned)}</p>
                    <p className="text-[10px] text-green-500">{ledgerStats.total_cylinders} cyl</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 p-2.5 rounded-lg" data-testid="sp-total-deducted">
                    <p className="text-[10px] text-red-600 uppercase font-semibold">Deducted</p>
                    <p className="text-base sm:text-lg font-bold text-red-700">{formatCurrency(ledgerStats.total_deducted)}</p>
                  </div>
                  <div className="bg-indigo-50 border border-indigo-200 p-2.5 rounded-lg" data-testid="sp-net-earning">
                    <p className="text-[10px] text-indigo-600 uppercase font-semibold">Net Commission</p>
                    <p className="text-base sm:text-lg font-bold text-indigo-700">{formatCurrency(ledgerStats.net_earning)}</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 p-2.5 rounded-lg" data-testid="sp-total-paid">
                    <p className="text-[10px] text-blue-600 uppercase font-semibold">Paid</p>
                    <p className="text-base sm:text-lg font-bold text-blue-700">{formatCurrency(ledgerStats.total_paid)}</p>
                    <p className="text-[10px] text-blue-500">{ledgerStats.payment_count} payments</p>
                  </div>
                  <div className={`p-2.5 rounded-lg border-2 ${(ledgerStats.pending_payment || 0) > 0 ? 'bg-orange-50 border-orange-300' : 'bg-emerald-50 border-emerald-300'}`} data-testid="sp-pending-payment">
                    <p className={`text-[10px] uppercase font-semibold ${(ledgerStats.pending_payment || 0) > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>
                      {(() => {
                        const pending = ledgerStats.pending_payment || 0;
                        if (pending > 0) return 'Pending';
                        if (pending < 0) return 'Advance';
                        return 'Settled';
                      })()}
                    </p>
                    <p className={`text-base sm:text-lg font-bold ${(() => {
                      const pending = ledgerStats.pending_payment || 0;
                      if (pending > 0) return 'text-orange-700';
                      if (pending < 0) return 'text-emerald-700';
                      return 'text-gray-700';
                    })()}`}>
                      {formatCurrency(Math.abs(ledgerStats.pending_payment || 0))}
                    </p>
                  </div>
                </div>
              )}

              {/* Record Payment Form — Admin/Owner only */}
              {isOwner && (
                <form onSubmit={handleRecordPayment} className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-xs font-bold text-gray-600 mb-2">Record Payment</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <Input type="number" step="0.01" min="1" placeholder="Amount" value={paymentForm.amount} onChange={e => setPaymentForm(p => ({ ...p, amount: e.target.value }))} required className="h-9 text-sm" data-testid="sp-pay-amount" />
                    </div>
                    <div>
                      <select value={paymentForm.payment_mode} onChange={e => setPaymentForm(p => ({ ...p, payment_mode: e.target.value }))} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm">
                        <option value="cash">Cash</option>
                        <option value="upi">UPI</option>
                        <option value="bank">Bank Transfer</option>
                        <option value="cheque">Cheque</option>
                      </select>
                    </div>
                    <div>
                      <Input placeholder="Reference/UTR" value={paymentForm.reference} onChange={e => setPaymentForm(p => ({ ...p, reference: e.target.value }))} className="h-9 text-sm" data-testid="sp-pay-ref" />
                    </div>
                    <div>
                      <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 h-9 text-xs" disabled={paymentSubmitting || !paymentForm.amount} data-testid="sp-pay-submit">
                        {paymentSubmitting ? 'Saving...' : 'Record Payment'}
                      </Button>
                    </div>
                  </div>
                </form>
              )}

              <div className="mt-3 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">Type</TableHead>
                      <TableHead className="text-xs">Agency/Details</TableHead>
                      <TableHead className="text-xs">TXN ID</TableHead>
                      <TableHead className="text-xs text-right">Qty</TableHead>
                      <TableHead className="text-xs text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledgerEntries.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-6 text-gray-400">No entries yet</TableCell></TableRow> : ledgerEntries.map(e => (
                      <TableRow key={e.id} className={e.type === 'payment' ? 'bg-blue-50/50' : ''}>
                        <TableCell className="text-xs">{e.booking_date || formatDate(e.created_at)}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-bold ${
                            e.type === 'earned' ? 'bg-green-100 text-green-700' :
                            e.type === 'payment' ? 'bg-blue-100 text-blue-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {e.type === 'earned' ? <><ArrowUp size={10} /> Earned</> :
                             e.type === 'payment' ? <><ArrowDown size={10} /> Paid</> :
                             <><ArrowDown size={10} /> Deducted</>}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">
                          {e.type === 'payment' ? (
                            <span className="text-blue-700 text-xs">{e.reason}{e.paid_by && ` — by ${e.paid_by}`}</span>
                          ) : e.agency_name}
                        </TableCell>
                        <TableCell className="text-xs font-mono text-gray-500">{e.transaction_id}</TableCell>
                        <TableCell className="text-right text-sm font-bold">{e.quantity || '—'}</TableCell>
                        <TableCell className={`text-right font-semibold text-sm ${e.type === 'payment' ? 'text-blue-700' : e.type === 'earned' ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(e.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* KYC Dialog */}
      <Dialog open={kycOpen} onOpenChange={setKycOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          {kycPerson && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <IdentificationCard size={22} className="text-amber-600" />
                  KYC — {kycPerson.name}
                </DialogTitle>
              </DialogHeader>

              {/* Current KYC Status */}
              <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">KYC Status</span>
                  <span className={`text-xs px-2 py-0.5 rounded font-semibold inline-flex items-center gap-1 ${
                    kycData.kyc_status === 'verified' ? 'bg-green-100 text-green-700' :
                    kycData.kyc_status === 'submitted' ? 'bg-amber-100 text-amber-700' :
                    'bg-gray-100 text-gray-500'
                  }`} data-testid="kyc-status-badge">
                    {kycData.kyc_status === 'verified' ? <CheckCircle size={12} /> : <Clock size={12} />}
                    {(() => {
                      if (kycData.kyc_status === 'verified') return 'Verified';
                      if (kycData.kyc_status === 'submitted') return 'Under Review';
                      return 'Pending';
                    })()}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-sm text-gray-600">KYC Type</span>
                  <span className="text-xs font-semibold text-gray-700">
                    {(() => {
                      if (kycData.kyc_type === 'none') return 'Not Submitted';
                      if (kycData.kyc_type === 'mini') return 'Mini KYC';
                      return 'Full KYC';
                    })()}
                  </span>
                </div>
                {kycData.pan_number && (
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm text-gray-600">PAN</span>
                    <span className="text-xs font-mono text-gray-700">{kycData.pan_number}</span>
                  </div>
                )}
                {kycData.gst_number && (
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm text-gray-600">GST</span>
                    <span className="text-xs font-mono text-gray-700">{kycData.gst_number}</span>
                  </div>
                )}
              </div>

              {/* KYC Form */}
              {kycData.kyc_status !== 'verified' && (
                <div className="mt-4 space-y-3">
                  <div>
                    <Label>KYC Type</Label>
                    <Select value={kycForm.kyc_type} onValueChange={v => setKycForm(p => ({ ...p, kyc_type: v }))}>
                      <SelectTrigger data-testid="kyc-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mini">Mini KYC (Name + Mobile)</SelectItem>
                        <SelectItem value="full">Full KYC (PAN + GST + Docs)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {kycForm.kyc_type === 'full' && (
                    <>
                      <div>
                        <Label>PAN Number</Label>
                        <Input value={kycForm.pan_number} onChange={e => setKycForm(p => ({ ...p, pan_number: e.target.value.toUpperCase() }))} placeholder="ABCDE1234F" maxLength={10} data-testid="kyc-pan" />
                      </div>
                      <div>
                        <Label>GST Number</Label>
                        <Input value={kycForm.gst_number} onChange={e => setKycForm(p => ({ ...p, gst_number: e.target.value.toUpperCase() }))} placeholder="22AAAAA0000A1Z5" maxLength={15} data-testid="kyc-gst" />
                      </div>
                    </>
                  )}
                  <Button onClick={handleKycSubmit} disabled={kycSubmitting} className="w-full bg-amber-600 hover:bg-amber-700 text-white h-10" data-testid="kyc-submit-btn">
                    {kycSubmitting ? 'Saving...' : 'Submit KYC'}
                  </Button>
                </div>
              )}

              {/* Admin Verify Button */}
              {isOwner && kycData.kyc_status === 'submitted' && (
                <Button onClick={handleKycVerify} className="w-full bg-green-600 hover:bg-green-700 text-white h-10 mt-3" data-testid="kyc-verify-btn">
                  <CheckCircle size={16} className="mr-2" /> Verify KYC
                </Button>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
